import moment from 'moment';
import {
  getTodosWithAddedNesting,
  getTodosAfterDrop,
  getTodosWithRemovedNesting,
  getUpdatedTodos,
} from 'Shared/selectors/todos';
import { getItemsForStandardViewWithMeetingMode } from 'Shared/selectors/workspace';
import uniqBy from 'lodash/uniqBy';

// Related to todos components.
// NOTE: At this point, we're making this reducer into a nested
// reducer.
export function todos(state = [], action) {
  switch (action.type) {
    case 'MEETING_ITEM_DROP_REQUEST':
      return getTodosAfterDrop(state, action.payload);
    case 'ADD_MEETING_ITEM_ATTACHMENT_RESPONSE':
      return state.map(meetingItem => {
        if (meetingItem.uuid === action.payload.uuid) {
          return { ...meetingItem, attachments: uniqBy([...meetingItem.attachments, action.payload.attachment], 'id') };
        }
        if (meetingItem.uuid === action.payload.parentUuid)
          return { ...meetingItem, updatedAt: action.payload.parentUpdatedAt };
        return meetingItem;
      });
    case 'UPDATE_MEETING_ITEM_RESPONSE':
    case 'MEETING_ITEM_UPDATE_REQUEST':
      return getUpdatedTodos(state, action.payload);
    case 'UPDATE_MEETING_ITEM_ERROR':
      return state.map(item => (item.uuid === action.payload.uuid ? action.payload : item));
    case 'CREATE_WORKSPACE_COMMENT_REQUEST':
    case 'DESTROY_WORKSPACE_COMMENT_ERROR':
      return state.map(meetingItem => {
        if (meetingItem.id === action.payload.meetingItem.id) {
          return { ...meetingItem, comments: [...meetingItem.comments, action.payload.workspaceComment] };
        }

        return meetingItem;
      });
    case 'CREATE_WORKSPACE_COMMENT_RESPONSE':
      return state.map(meetingItem => {
        if (meetingItem.id === action.payload.meetingItem.id) {
          return {
            ...meetingItem,
            commentsCount: meetingItem.commentsCount + 1,
          };
        }
        return meetingItem;
      });
    case 'UPDATE_COMMENT_ITEM_RESPONSE':
      return state.map(meetingItem => {
        if (meetingItem.id === action.payload.meetingItem.parentId) {
          return { ...meetingItem, updatedAt: action.payload.workspaceComment.meetingItemUpdatedAt };
        }
        if (meetingItem.id === action.payload.meetingItem.id) {
          return {
            ...meetingItem,
            comments: meetingItem.comments.map(comment => {
              if (comment.uuid === action.payload.workspaceComment.uuid) return action.payload.workspaceComment;
              return comment;
            }),
          };
        }
        return meetingItem;
      });
    case 'CREATE_WORKSPACE_COMMENT_ERROR':
    case 'DESTROY_WORKSPACE_COMMENT_REQUEST':
    case 'DESTROY_WORKSPACE_COMMENT_RESPONSE':
      return state.map(meetingItem => {
        if (meetingItem.id === action.payload.meetingItem.id) {
          return {
            ...meetingItem,
            comments: meetingItem.comments.filter(comment => comment.uuid !== action.payload.workspaceComment.uuid),
            commentsCount: meetingItem.commentsCount - 1,
          };
        }

        return meetingItem;
      });
    case 'REMOVE_COMMENT_ATTACHMENT_RESPONSE':
      return state.map(meetingItem => {
        if (meetingItem.id === action.payload.meetingItem.id) {
          return {
            ...meetingItem,
            comments: meetingItem.comments.map(comment => {
              if (comment.uuid === action.payload.commentUuid) {
                return {
                  ...comment,
                  attachments: comment.attachments.filter(attachment => attachment.id !== action.payload.attachmentId),
                };
              }

              return comment;
            }),
          };
        }
        return meetingItem;
      });
    case 'DUPLICATE_ACROSS_WORKSPACES_RESPONSE':
      return action.payload.isMoveItems ? state.filter(({ uuid }) => !action.payload.itemUuids.includes(uuid)) : state;
    case 'MEETING_ITEM_REMOVE_ATTACHMENT_RESPONSE':
      return state.map(meetingItem => {
        if (meetingItem.uuid === action.payload.uuid) {
          return {
            ...meetingItem,
            attachments: meetingItem.attachments.filter(attachment => attachment.id !== action.payload.attachmentId),
          };
        }
        return meetingItem;
      });
    case 'BULK_UPDATE_MEETING_ITEM_RESPONSE':
      return state.map(todo => {
        const newTodo = action.payload.find(({ uuid }) => uuid === todo.uuid);
        if (newTodo) return newTodo;
        return todo;
      });
    case 'MEETING_ITEM_BULK_UPDATE_REQUEST':
      return state.map(todo => {
        if (action.payload.selectedUuids.includes(todo.uuid)) return { ...todo, ...action.payload.params };
        return todo;
      });
    case 'MEETING_ITEM_BULK_REMOVE_REQUEST':
      return state
        .filter(({ uuid }) => !action.payload.selectedUuids.includes(uuid))
        .map((item, index) => ({ ...item, position: index + 1 }));
    case 'MEETING_ITEM_REMOVE_REQUEST':
      return state
        .filter(({ uuid }) => uuid !== action.payload.uuid)
        .map((item, index) => ({ ...item, position: index + 1 }));
    case 'MEETING_ITEM_REMOVE_ERROR':
      return [...state, action.payload.item];
    case 'MEETING_ITEM_NEST_REQUEST':
      return getTodosWithAddedNesting(state, action.payload);
    case 'MEETING_ITEM_SET_COMPLETED_REQUEST':
    case 'MEETING_ITEM_SET_COMPLETED_ERROR':
      return state.map(todo => {
        if (todo.uuid === action.payload.uuid) {
          return {
            ...todo,
            completed: action.payload.completed,
            completedAt: action.payload.completed ? moment().unix() : null,
            completedBy: action.payload.completedBy,
          };
        }
        return todo;
      });
    case 'MEETING_ITEM_SET_COMPLETED_RESPONSE':
      return state.map(item => {
        const updatedItem = action.payload.find(toggledItem => toggledItem.uuid === item.uuid);
        return updatedItem
          ? {
              ...item,
              completed: updatedItem.completed,
              completedAt: updatedItem.completedAt,
              completedBy: updatedItem.completedBy,
            }
          : item;
      });
    case 'MEETING_ITEM_REMOVE_NESTING_REQUEST':
      return getTodosWithRemovedNesting(state, action.payload);
    case 'MEETING_ITEM_ADD_RESPONSE':
      return state.map(item => {
        if (item.uuid === action.payload.uuid)
          return {
            ...action.payload,
            isShowLoader: false,
            body: item.body,
            parentId: item.parentId,
            parentUuid: item.parentUuid,
          };
        return item;
      });
    case 'MEETING_ITEM_ADD_REQUEST': {
      const visibleItems = getItemsForStandardViewWithMeetingMode(state);
      const newItemsList = [...visibleItems, ...state.filter(i => !visibleItems.includes(i))];
      const insertIndex = action.payload.positionForActionItemsPage
        ? newItemsList.map(i => i.positionForActionItemsPage).indexOf(action.payload.positionForActionItemsPage - 1)
        : newItemsList.map(i => i.position).indexOf(action.payload.position - 1);

      newItemsList.splice(insertIndex + 1, 0, action.payload);

      return newItemsList.map((item, index) => ({
        ...item,
        position: index + 1,
      }));
    }
    case 'MEETING_ITEM_ADD_ERROR':
      return state.filter(({ uuid }) => uuid !== action.payload.uuid);
    case 'MEETING_ITEM_WITH_CHILD_ADD_REQUEST':
      return [...action.payload, ...state].map((item, index) => ({
        ...item,
        position: index + 1,
      }));
    case 'MEETING_ITEM_WITH_CHILD_ADD_RESPONSE':
      return state.map(item => {
        const newItem = action.payload.find(({ uuid }) => item.uuid === uuid);
        if (newItem) return newItem;
        return item;
      });
    case 'MEETING_ITEM_WITH_CHILD_ADD_ERROR':
      return state.filter(item => !action.payload.find(({ uuid }) => item.uuid === uuid));
    case 'MEETING_ITEM_BULK_ADD_REQUEST': {
      const { currentUuid, items } = action.payload;
      if (currentUuid) {
        const index = state.findIndex(i => i.uuid === currentUuid);
        return [...state.slice(0, index + 1), ...items, ...state.slice(index + 1)];
      }
      return [...items, ...state];
    }
    case 'MEETING_ITEM_BULK_ADD_RESPONSE':
      return state.map(item => {
        const newItem = action.payload.find(i => i.uuid === item.uuid);
        if (newItem) {
          return newItem;
        }
        return item;
      });
    case 'MEETING_ITEM_BULK_ADD_ERROR':
      return state.filter(item => !action.payload.uuids.includes(item.uuid));
    case 'UPDATE_MEETING_ITEM_RECURRENCE':
    case 'REMOVE_MEETING_ITEM_RECURRENCE': {
      return state.map(todo => {
        if (todo.uuid === action.payload.uuid || (!action.payload.isEdit && todo.parentUuid === action.payload.uuid)) {
          return {
            ...todo,
            recurringWorkspaceItem: action.payload.recurringWorkspaceItem,
          };
        }
        return todo;
      });
    }
    default:
      return state;
  }
}
